import type {
  AppPageProps,
  RenderDocumentProps,
  RenderingStrategy,
  SlugPageProps,
} from '@/cms/rendering-strategy/types'
import { getFonts } from '@/framework/react-styles'
import { getBrandName } from '@/helpers/environment'
import featureFlagClient from '@/scopes/common/infrastructure/feature-flags/featureFlagClient'
import { createConsoleLogger, PageComponent, Placeholder, UniformContextProvider } from '@uniformdev/next'
import * as React from 'react'

export function mvcRenderingStrategy(): RenderingStrategy {
  function renderDocument({ Html, Main, head, pageProps }: RenderDocumentProps) {
    const { page, metadata } = pageProps

    const { name, template, fields } = page || {}
    const { bodyAttrs, htmlLang } = fields || {}
    const { machineName, staticFileName } = bodyAttrs || {}

    // TODO use brand name from config instead; Now 404 and 500 pages use this
    const brandName = getBrandName()
    const { primaryFont, secondaryFont } = getFonts(brandName)

    const optimizelySdkKey = process.env.NEXT_PUBLIC_OPTIMIZELY_SDK_KEY ?? ''

    return (
      <Html lang={htmlLang}>
        <head>
          {head}
          {metadata?.title && <title>{metadata.title}</title>}
          {metadata?.faviconUrl && <link rel="shortcut icon" type="image/x-icon" href={metadata.faviconUrl} />}
        </head>
        <body
          className={`o-block o-block--stretch l-site t-${staticFileName} ${primaryFont.variable} ${secondaryFont.variable}`}
          data-in-name={machineName}
          data-qa-page={name}
          data-qa-template={template}
        >
          <Main />
          {featureFlagClient.isActive('MVC_PAGES_LOAD_OPTIMIZELY_SDK') && (
            <>
              <script src="https://unpkg.com/@optimizely/optimizely-sdk/dist/optimizely.browser.umd.min.js" crossOrigin="anonymous" />
              <script
                dangerouslySetInnerHTML={{
                  __html:
                  // language=JavaScript
                    `
                    (function() {
                      var sdkKey = '${optimizelySdkKey}';

                      if (!window.optimizelySdk) {
                        console.error('Optimizely SDK not loaded');
                        return;
                      }

                      if (!sdkKey) {
                        console.error('Optimizely SDK key is missing');
                        return;
                      }

                      var pollingConfigManager = window.optimizelySdk.createPollingProjectConfigManager({
                        sdkKey: sdkKey,
                        autoUpdate: false,
                        updateInterval: 60000,
                      });

                      var batchEventProcessor = window.optimizelySdk.createBatchEventProcessor();
                      var odpManager = window.optimizelySdk.createOdpManager();

                      var optimizelyJSClient = window.optimizelySdk.createInstance({
                        projectConfigManager: pollingConfigManager,
                        eventProcessor: batchEventProcessor,
                        odpManager: odpManager,
                      });

                      optimizelyJSClient
                        .onReady()
                        .then(function() {
                          window.optimizelyJSClient = optimizelyJSClient;
                          window.dispatchEvent(new Event('optimizely:ready'));
                          console.log('Optimizely client ready');
                        })
                        .catch(function(err) {
                          console.error('Error initializing Optimizely client:', err);
                        });
                    })();
                `,
                }}
              />
            </>
          )}
        </body>
      </Html>
    )
  }

  function renderApp({ Component, pageProps }: AppPageProps) {
    return (
      <>
        <Component {...pageProps} />
      </>
    )
  }

  function renderPageComponent(pageProps: SlugPageProps) {
    return (
      <UniformContextProvider logger={createConsoleLogger()} componentMap={{}}>
        <PageComponent {...pageProps}>
          {renderingContext => <Placeholder placeholderKey="/" renderingContext={renderingContext} />}
        </PageComponent>
      </UniformContextProvider>
    )
  }

  return {
    type: 'mvc',
    renderDocument,
    renderApp,
    renderPageComponent,
  }
}
